import { all, call, fork, takeEvery } from "@redux-saga/core/effects";
import {
  createResourceDependencies as createResourceDependenciesApi,
  createResourceDependency as createResourceDependencyApi,
  deleteResourceDependencies as deleteResourceDependenciesApi,
  deleteResourceDependency as deleteResourceDependencyApi,
  updateResourceDependency as updateResourceDependencyApi,
  getResourceDependencies
} from "api/ResourceDependenciesApi";
import {
  CREATE_RESOURCE_DEPENDENCY,
  CREATE_RESOURCE_DEPENDENCIES,
  DELETE_RESOURCE_DEPENDENCY,
  DELETE_RESOURCE_DEPENDENCIES,
  SYNC_FLOW_INTEGRATION_DEPENDENCIES,
  UPDATE_RESOURCE_DEPENDENCY
} from "redux/constants/portal";
import {
  getResourceDependencyContextId,
  getResourceDependencySourceKey
} from "helpers/resourceDependencies";
import { addErrorLog } from "Logger/logger";

const pendingDependencyKeys = new Set();

const normalizeIntegrationIds = (value) => {
  if (!value) return [];

  let integrationIds = value;
  if (typeof value === "string") {
    try {
      integrationIds = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(integrationIds)) return [];

  return [...new Set(integrationIds.filter(Boolean))];
};

function* deleteDependency(payload) {
  const response = yield call(deleteResourceDependencyApi, payload);

  if (!response?.isSuccessful) {
    throw new Error(response?.message || "Delete resource dependency failed");
  }
}

function* handleCreateResourceDependency({ payload }) {
  const forwardQuery = {
    tenantId: payload.tenantId,
    sourceId: payload.sourceId,
    sourceResourceType: payload.sourceResourceType,
    direction: "forward",
    targetResourceType: payload.targetResourceType
  };
  const cacheKey = getResourceDependencySourceKey(forwardQuery);
  const dependencyKey = `${cacheKey}:${payload.targetResourceType}:${payload.targetId}:${payload.contextId}`;

  try {
    if (pendingDependencyKeys.has(dependencyKey)) return;

    pendingDependencyKeys.add(dependencyKey);
    const response = yield call(createResourceDependencyApi, payload);

    if (!response?.isSuccessful) {
      throw new Error(response?.message || "Create resource dependency failed");
    }

  } catch (error) {
    addErrorLog("handleCreateResourceDependency", error, payload.tenantId);
  } finally {
    pendingDependencyKeys.delete(dependencyKey);
  }
}

function* handleCreateResourceDependencies({ payload }) {
  try {
    const response = yield call(createResourceDependenciesApi, payload);

    if (!response?.isSuccessful) {
      throw new Error(response?.message || "Create resource dependencies failed");
    }

  } catch (error) {
    addErrorLog("handleCreateResourceDependencies", error, payload.tenantId);
  }
}


function* handleDeleteResourceDependency({ payload }) {
  try {
    yield call(deleteDependency, payload);
  } catch (error) {
    addErrorLog("handleDeleteResourceDependency", error, payload.tenantId);
  }
}

function* handleDeleteResourceDependencies({ payload }) {
  try {
    const response = yield call(deleteResourceDependenciesApi, payload);

    if (!response?.isSuccessful) {
      throw new Error(response?.message || "Delete resource dependencies failed");
    }

  } catch (error) {
    addErrorLog("handleDeleteResourceDependencies", error, payload.tenantId);
  }
}

function* handleUpdateResourceDependency({ payload }) {
  const { tenantId, newInput } = payload;
  const cacheKeyNew = getResourceDependencySourceKey({
    tenantId,
    sourceId: newInput.sourceId,
    sourceResourceType: newInput.sourceResourceType,
    direction: "forward"
  });

  const dependencyKey = `${cacheKeyNew}:${newInput.targetResourceType}:${newInput.targetId}:${newInput.contextId}`;

  try {
    if (pendingDependencyKeys.has(dependencyKey)) return;

    pendingDependencyKeys.add(dependencyKey);
    const response = yield call(updateResourceDependencyApi, payload);

    if (!response?.isSuccessful) {
      throw new Error(response?.message || "Update resource dependency failed");
    }

  } catch (error) {
    addErrorLog("handleUpdateResourceDependency", error, tenantId);
  } finally {
    pendingDependencyKeys.delete(dependencyKey);
  }
}

function* handleSyncFlowIntegrationDependencies({ payload }) {
  const nextIntegrationIds = normalizeIntegrationIds(payload.nextIntegrationIds);
  const nextIds = new Set(nextIntegrationIds);
  const contextId = getResourceDependencyContextId(
    payload.flowId,
    "integration"
  );

  try {
    const currentDependenciesResponse = yield call(getResourceDependencies, {
      tenantId: payload.tenantId,
      sourceId: payload.flowId,
      direction: "forward",
      targetResourceType: "integration"
    });

    if (!currentDependenciesResponse?.isSuccessful) {
      throw new Error(
        currentDependenciesResponse?.message ||
          "Retrieve flow integration dependencies failed"
      );
    }

    const currentDependencies = Array.isArray(
      currentDependenciesResponse.resultObject
    )
      ? currentDependenciesResponse.resultObject
      : [];
    const currentCanonicalIds = new Set(
      currentDependencies
        .filter((dependency) => dependency.context_id === contextId)
        .map((dependency) => dependency.target_id)
    );
    const createDependencies = nextIntegrationIds
      .filter((integrationId) => !currentCanonicalIds.has(integrationId))
      .map((integrationId) => ({
        sourceId: payload.flowId,
        targetId: integrationId,
        sourceResourceType: "flow",
        targetResourceType: "integration",
        contextId
      }));
    const deleteDependencies = currentDependencies
      .filter(
        (dependency) =>
          !nextIds.has(dependency.target_id) ||
          dependency.context_id !== contextId
      )
      .map((dependency) => ({
        sourceId: dependency.source_id,
        targetId: dependency.target_id,
        sourceResourceType: dependency.source_resource_type,
        targetResourceType: dependency.target_resource_type,
        contextId: dependency.context_id
      }));

    if (createDependencies.length) {
      const createResponse = yield call(createResourceDependenciesApi, {
        tenantId: payload.tenantId,
        dependencies: createDependencies
      });

      if (!createResponse?.isSuccessful) {
        throw new Error(
          createResponse?.message || "Create resource dependencies failed"
        );
      }

    }

    if (deleteDependencies.length) {
      const deleteResponse = yield call(deleteResourceDependenciesApi, {
        tenantId: payload.tenantId,
        dependencies: deleteDependencies
      });

      if (!deleteResponse?.isSuccessful) {
        throw new Error(
          deleteResponse?.message || "Delete resource dependencies failed"
        );
      }

    }
  } catch (error) {
    addErrorLog("handleSyncFlowIntegrationDependencies", error, payload.tenantId);
  }
}

function* watchCreateResourceDependency() {
  yield takeEvery(CREATE_RESOURCE_DEPENDENCY, handleCreateResourceDependency);
}

function* watchCreateResourceDependencies() {
  yield takeEvery(CREATE_RESOURCE_DEPENDENCIES, handleCreateResourceDependencies);
}
function* watchDeleteResourceDependency() {
  yield takeEvery(DELETE_RESOURCE_DEPENDENCY, handleDeleteResourceDependency);
}

function* watchDeleteResourceDependencies() {
  yield takeEvery(DELETE_RESOURCE_DEPENDENCIES, handleDeleteResourceDependencies);
}

function* watchUpdateResourceDependency() {
  yield takeEvery(UPDATE_RESOURCE_DEPENDENCY, handleUpdateResourceDependency);
}

function* watchSyncFlowIntegrationDependencies() {
  yield takeEvery(
    SYNC_FLOW_INTEGRATION_DEPENDENCIES,
    handleSyncFlowIntegrationDependencies
  );
}

export function* rootResourceDependenciesSaga() {
  yield all([
    fork(watchCreateResourceDependency),
    fork(watchCreateResourceDependencies),
    fork(watchDeleteResourceDependency),
    fork(watchDeleteResourceDependencies),
    fork(watchUpdateResourceDependency),
    fork(watchSyncFlowIntegrationDependencies)
  ]);
}
